import { Kind } from "@/services/backend/samples/types/kind";
import { EntityId } from "@/shared/types/entity-id";
import { Namespace } from "@/shared/types/namespace";

/**
 * Sample is a base interface for all samples. It should not be used directly.
 */
export interface Sample {
  id: string;
  documentId: EntityId;
  documentNamespace: Namespace | null;
  number: string;
  kind: Kind;
  location: string;
  spot: string;
  labId: EntityId;
  comment: string;
  createdAt: string | null;
}

export interface BaseSamplePatchRequest {
  id: string;
  number?: string;
  labId?: string;
  comment?: string;
}

export function newSample(
  id: string,
  number: string,
  kind: Kind,
  documentId: EntityId,
  documentNamespace: Namespace | null,
): Sample {
  return {
    id,
    documentId,
    documentNamespace,
    number,
    kind,
    location: "",
    spot: "",
    labId: null,
    comment: "",
    createdAt: new Date().toISOString(),
  };
}

export function generateSampleNumber(): string {
  const date = new Date();
  const random = Math.floor(Math.random() * 1_000);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.${random}`;
}

export function updateSampleDocument<T extends Sample>(
  sample: T,
  id: EntityId,
  namespace: Namespace,
): T {
  return {
    ...sample,
    documentId: id,
    documentNamespace: namespace,
  };
}

export function updateSampleNumber<T extends Sample>(
  sample: T,
  number: string,
): T {
  return {
    ...sample,
    number,
  };
}

export function updateSampleLab<T extends Sample>(
  sample: T,
  labId: EntityId,
): T {
  return {
    ...sample,
    labId,
  };
}

export function updateSampleComment<T extends Sample>(
  sample: T,
  comment: string,
): T {
  return {
    ...sample,
    comment,
  };
}

export function updateSampleCreatedAt<T extends Sample>(
  sample: T,
  createdAt: string | null,
): T {
  return {
    ...sample,
    createdAt,
  };
}

export function updateLocation<T extends { location: string }>(
  sample: T,
  location: string,
): T {
  return {
    ...sample,
    location,
  };
}

export function updateSpot<T extends { spot: string }>(
  sample: T,
  spot: string,
): T {
  return {
    ...sample,
    spot,
  };
}

export function updateSampleTakers<T extends { sampleTakers: string }>(
  sample: T,
  sampleTakers: string,
): T {
  return {
    ...sample,
    sampleTakers,
  };
}
